import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Returns a "back" action that goes to the previous in-app page when there is one.
 * When the page was opened directly (a link from an email or a push notification,
 * a cold PWA start) it opens `fallbackPath` instead, so the user never leaves the app.
 * The fallback replaces the current entry, so going back from it never returns here.
 * Without a fallback it does nothing, like a browser's back button at the start of its history.
 */
export const useHistoryBack = (fallbackPath?: string) => {
  const navigate = useNavigate();

  return useCallback(() => {
    // react-router keeps an incrementing index on the history state entry.
    // idx > 0 means there is a previous in-app entry we can safely return to.
    const historyIndex = (window.history.state?.idx as number | undefined) ?? 0;

    if (historyIndex > 0) {
      navigate(-1);
    } else if (fallbackPath) {
      navigate(fallbackPath, { replace: true });
    }
  }, [navigate, fallbackPath]);
};
